import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hydrateCart, mergeDeviceCartIntoAccount } from '../../ducks/cart.duck';

/**
 * Owns the cart's two side effects and renders nothing.
 *
 * Mount this exactly once per page — the topbar is on every page, so it lives there.
 * It cannot live in CartIcon, which renders in both the mobile and the desktop
 * topbar and would run both effects twice.
 *
 * The merge is keyed on the current user going from absent to present rather than on
 * mount, because the path that matters most is a guest who fills a cart, clicks
 * "Sign in to check out this box" and signs in without the page ever reloading. The
 * topbar never unmounts there, so a mount-only trigger would never fire and the buyer
 * would watch their cart come back empty. Keying on the user id also covers password
 * login, IdP signup and the SSO cold boot with one mechanism.
 *
 * This deliberately does not live in a duck: cart.duck imports setCurrentUser from
 * user.duck, so a hook there is a circular import, and auth.duck misses the SSO cold
 * boot entirely.
 *
 * @component
 * @returns {null}
 */
const CartLifecycle = () => {
  const dispatch = useDispatch();
  const currentUserId = useSelector(state => state.user?.currentUser?.id?.uuid || null);
  const deviceRowCount = useSelector(state => state.cart?.deviceRows?.length || 0);

  // The server has no idea whether a cart exists, so the device cart is read after mount.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    dispatch(hydrateCart());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seeded with the id we mounted with: a buyer who was already signed in never
  // transitioned, and hydrateCart's own merge branch covers them.
  const previousUserIdRef = useRef(currentUserId);
  const signedInUserIdRef = useRef(null);
  const mergedForUserIdRef = useRef(null);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = currentUserId;

    if (!previousUserId && currentUserId) {
      signedInUserIdRef.current = currentUserId;
    }

    // Device rows can land after the transition, since hydration is asynchronous. The
    // transition is latched so a late arrival still merges, and merging is recorded per
    // user so a second rows update cannot merge the same sign-in twice.
    const justSignedIn = currentUserId && signedInUserIdRef.current === currentUserId;
    if (!justSignedIn || mergedForUserIdRef.current === currentUserId || deviceRowCount === 0) {
      return;
    }

    mergedForUserIdRef.current = currentUserId;
    dispatch(mergeDeviceCartIntoAccount())
      .then(action => {
        if (action?.meta?.requestStatus === 'rejected' || action?.error) {
          if (mergedForUserIdRef.current === currentUserId) {
            mergedForUserIdRef.current = null;
          }
        }
      })
      .catch(() => {
        if (mergedForUserIdRef.current === currentUserId) {
          mergedForUserIdRef.current = null;
        }
      });
  }, [currentUserId, deviceRowCount, dispatch]);

  return null;
};

export default CartLifecycle;
